import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { DocumentHead, MainContainer, PageTemplate } from 'lib/components';
import { useAuth } from 'lib/context';
import { Trans, useTranslation } from 'react-i18next';
import { Container, Wrapper } from './styles';
import { Video } from '../components';
import { BackToIntegrations } from '../components/BackToIntegrations';
import { Gap } from 'lib/components/styles/layout';
import { IoMdLaptop, IoMdMail } from 'react-icons/io';
import { ModalContactAM } from 'lib/components/modal/ModalContactAM';
import { IntegrationHeader } from '../components/IntegrationHeader';
import { Button } from 'react-covideo-common';
import { Description } from 'app/pages/library/components/boardModal/style';
import { BUTTON_VARIANT } from 'react-covideo-common/dist/components/Button/types';
import { useIntegrationTypeFilter } from '../main/hooks';
import { INTEGRATION_TYPE } from 'lib/api/integrations/types';

export enum ActionTypes {
  CONTACT = 'contact',
  INSTALL = 'install',
  DEMO = 'demo',
  DOWNLOAD = 'download',
  CONNECT = 'connect',
}

interface IntegrationDetailsPageProps {
  onClick?: any;
  actionType?: ActionTypes;
  connectSocialProfile?: boolean;
  btnText?: string;
  btnVariant?: BUTTON_VARIANT;
  isBtnDisabled?: boolean;
}

export const IntegrationDetailsPage = ({
  onClick,
  actionType,
  connectSocialProfile = false,
  btnText,
  isBtnDisabled = false,
  btnVariant = 'primary',
}: IntegrationDetailsPageProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { userData } = useAuth();
  const isAutomotive = userData.isAutomotive;
  const slug = location.pathname.split('/')[2];
  const filter = useIntegrationTypeFilter();

  const [showContactForm, setShowContactForm] = useState(false);

  const integration = (
    isAutomotive
      ? filter([INTEGRATION_TYPE.AUTOMOTIVE])
      : filter([INTEGRATION_TYPE.UNIVERSAL])
  ).find(ig => ig.key === slug);

  const getActionButton = () => {
    switch (actionType) {
      case ActionTypes.CONTACT:
        return (
          <Button
            text={btnText || t('Request integration')}
            icon={<IoMdMail />}
            onClick={() => {
              setShowContactForm(true);
            }}
          />
        );
      case ActionTypes.INSTALL:
        return <Button text={t('Install')} onClick={onClick} />;
      case ActionTypes.DEMO:
        return (
          <Button
            text={t('Reach Out to Covideo')}
            icon={<IoMdLaptop />}
            onClick={() => {
              setShowContactForm(true);
            }}
          />
        );
      case ActionTypes.DOWNLOAD:
        return <Button text={t('Download')} onClick={onClick} />;
      case ActionTypes.CONNECT:
        return (
          <Button
            text={btnText || t('Connect')}
            disabled={isBtnDisabled}
            onClick={onClick}
            variant={btnVariant}
          />
        );
      default:
        <></>;
    }
  };

  return (
    <>
      <DocumentHead title={t('Integration')} />
      <PageTemplate
        modal={[]}
        main={
          <MainContainer>
            <Container>
              {integration && (
                <Wrapper>
                  {showContactForm && (
                    <ModalContactAM
                      handleModalClose={() => setShowContactForm(false)}
                      subject={`${integration.name} Integration`}
                    />
                  )}
                  <Gap flexDirection='row' justifyContent='space-between'>
                    <BackToIntegrations />
                    <Gap>
                      {getActionButton()}
                      {connectSocialProfile && (
                        <Button
                          text={t('Connect your social profile')}
                          onClick={() =>
                            history.push('/profile/social-profiles')
                          }
                        />
                      )}
                    </Gap>
                  </Gap>
                  <IntegrationHeader
                    imgSrc={integration?.imgSrc || ''}
                    title={integration?.name || ''}
                  />
                  <Gap
                    flexDirection='column'
                    width='720px'
                    gap='24px'
                    alignItems='flex-start'
                  >
                    {integration.videoSource && (
                      <Video
                        thumbnail={integration.thumbnail || ''}
                        videoSource={integration.videoSource}
                      />
                    )}

                    {integration.description.paragraphs.map(
                      (paragraphKey: string) => {
                        const href = integration.description.links
                          ? integration.description.links[paragraphKey]
                          : '';

                        return (
                          <Description key={paragraphKey}>
                            <Trans
                              i18nKey={`${integration.i18nBaseKey}.${paragraphKey}`}
                              components={
                                href
                                  ? [
                                      <a
                                        href={href}
                                        target='_blank'
                                        rel='noreferrer'
                                      >
                                        {' '}
                                      </a>,
                                    ]
                                  : []
                              }
                            />
                          </Description>
                        );
                      }
                    )}
                  </Gap>
                </Wrapper>
              )}
            </Container>
          </MainContainer>
        }
      />
    </>
  );
};
